/**
 * autoDetect.js
 *
 * Detect the active app and platform profile from live traffic.
 *
 * Strategy:
 *   - GAM requests are the most reliable signal: the `iu` parameter contains
 *     the full ad unit path, e.g. /4145/fnc/iosmob/hp/ban1
 *   - Extract networkId + publisher prefix → match against BUILTIN_APPS.adUnitPrefix
 *   - Extract the platform segment (3rd path component) → match against app.profiles
 *
 * Usage:
 *   const detected = detectProfileFromEntry(entry);   // { appId, profileId, appName, profileName } | null
 *   if (shouldAutoSwitch(detected)) {
 *     setActiveSelection(detected.appId, detected.profileId);
 *   }
 */

import { getAllApps, getActiveSelection, setActiveSelection } from '../config/appProfiles';

/* ─── IU parser ─────────────────────────────────────────────────────────────── */

/**
 * Extract the `iu` query parameter from a GAM URL and parse it into segments.
 * Returns null if the URL has no recognisable `iu`.
 */
function parseIU(url = '') {
  try {
    // iu may be URL-encoded; handle both forms
    const match = url.match(/[?&]iu=([^&]+)/);
    if (!match) return null;
    const iu = decodeURIComponent(match[1]);
    // Normalise leading slash
    return iu.replace(/^\//, '').split('/').filter(Boolean);
    // e.g. ['4145', 'generic', 'iosmob', 'hp', 'pol', 'ban1']
  } catch {
    return null;
  }
}

/* ─── Main detection ─────────────────────────────────────────────────────────── */

/**
 * Given a raw event entry (from the WebSocket auction stream), attempt to
 * identify the app and platform profile.
 *
 * Only GAM requests are used — they're the authoritative source of the IU.
 *
 * @param {object} entry  Raw event object (has url, requestType, validData, etc.)
 * @returns {{ appId, profileId, appName, profileName } | null}
 */
export function detectProfileFromEntry(entry) {
  if (!entry) return null;

  // Only inspect GAM ad requests (not impression/click beacons which have no iu)
  if (entry.requestType !== 'google') return null;

  const url = entry.url || entry.path || '';
  const parts = parseIU(url);
  if (!parts || parts.length < 3) return null;

  // parts[0] = networkId  (e.g. '4145')
  // parts[1] = publisher  (e.g. 'generic')
  // parts[2] = platform   (e.g. 'iosmob', 'droidmob', 'iostab', 'droidtab')
  const networkId = parts[0];
  const publisher = parts[1];
  const platform  = parts[2];

  // Build a normalised prefix for comparison: '4145/fnc'
  const detectedPrefix = `${networkId}/${publisher}`;

  const apps = getAllApps();

  for (const [appId, app] of Object.entries(apps)) {
    if (!app.adUnitPrefix) continue;

    // Normalise stored prefix: '/4145/fnc/' → '4145/fnc'
    const storedPrefix = app.adUnitPrefix.replace(/^\//, '').replace(/\/$/, '');

    if (storedPrefix !== detectedPrefix) continue;

    // App matched — now resolve the platform profile
    const profiles = app.profiles || {};

    let profileId = null;

    // 1. Exact match: platform segment === profile id (e.g. 'iosmob')
    if (platform && profiles[platform]) {
      profileId = platform;
    }

    // 2. Check the app taxonomy's platforms list and map to 'all' or first profile
    if (!profileId && app.taxonomy?.platforms?.includes(platform)) {
      // Platform is known for this app but has no exact profile key — use 'all' if available
      profileId = profiles.all ? 'all' : Object.keys(profiles)[0];
    }

    // 3. Fallback: use the first profile
    if (!profileId) {
      profileId = Object.keys(profiles)[0] || 'default';
    }

    const resolvedProfile = profiles[profileId] || {};

    return {
      appId,
      profileId,
      appName:     app.name,
      profileName: resolvedProfile.name || profileId,
      iuPrefix:    detectedPrefix,
      platform,
    };
  }

  return null;   // no matching app found
}

/* ─── Switch guard ───────────────────────────────────────────────────────────── */

/**
 * Returns true if we should auto-switch to `detected`.
 *
 * Rules:
 *  - Never switch if already on the detected app + platform (no-op)
 *  - Always switch if currently on 'generic' (no user selection made)
 *  - Switch if the app changes (traffic is clearly from a different app)
 *  - Switch if the platform changes within the same app
 *  - Do NOT switch if the user is currently on a custom app (respect custom selection)
 */
export function shouldAutoSwitch(detected) {
  if (!detected) return false;

  const { appId: currentApp, profileId: currentProfile } = getActiveSelection();

  // Already correct — nothing to do
  if (currentApp === detected.appId && currentProfile === detected.profileId) return false;

  // Always auto-detect from generic (default state)
  if (currentApp === 'generic') return true;

  // App changed (user is testing a different property)
  if (currentApp !== detected.appId) return true;

  // Same app, platform changed (e.g. switched from iPhone to Android during session)
  if (currentApp === detected.appId && currentProfile !== detected.profileId) return true;

  return false;
}

/* ─── One-shot initializer ───────────────────────────────────────────────────── */

// Tracks whether we've already fired a switch in this page session to avoid
// repeated switches while the user is manually browsing the app on the device.
let _lastAutoSwitchKey = null;

/**
 * Attempt auto-detection and switch if appropriate.
 * Calls setActiveSelection internally and returns the detected result (or null).
 * The caller is responsible for showing a toast / updating UI.
 *
 * @param {object} entry  Raw event entry
 * @returns {{ appId, profileId, appName, profileName } | null}  switched-to profile, or null
 */
export function tryAutoDetect(entry) {
  const detected = detectProfileFromEntry(entry);
  if (!detected) return null;
  if (!shouldAutoSwitch(detected)) return null;

  const switchKey = `${detected.appId}/${detected.profileId}`;
  if (_lastAutoSwitchKey === switchKey) return null;   // already switched to this; suppress repeat toast

  setActiveSelection(detected.appId, detected.profileId);
  _lastAutoSwitchKey = switchKey;
  return detected;
}

/** Reset the session guard — call this when the user manually clears traffic. */
export function resetAutoDetect() {
  _lastAutoSwitchKey = null;
}
